import React, {createContext, useContext, useState, useEffect, useMemo} from "react";
import { Message as MessageType } from "../../../../types/Message";
import { Company as CompanyType } from "../../../../types/Company";
import { User as UserType } from "../../../../types/User";
import { deleteMessage as deleteMessageApi, executeRun as executeRunApi, fetchMessagesByCompanyId, sendMessageToCompany, getUsersByCompany, getCompanyStatuses, getActiveCompanies, getUsersByJobTitleId } from "../../../../services/api";
import { CompanyStatus } from "../../../../types/CompanyStatuses";

interface ChatContextProps {
    companyId: string | null;
    company: CompanyType | null;
    setCompany: (company: CompanyType) => void;
    loading: boolean;
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    setCompanyId: (id: string) => void;
    messages: MessageType[];
    fetchMessages: (companyId: string) => void;
    sendMessage: (companyId: string, content: string) => Promise<MessageType | null>;
    executeRun: (companyId: string) => void;
    deleteMessage: (companyId: string, messageId: string) => Promise<void>;
    users: UserType[];
    setUsers: (users: UserType[]) => void;
    fetchUsersByCompany: (companyId: string) => Promise<void>;
    companyStatuses: CompanyStatus[];
    fetchCompanyStatuses: () => Promise<CompanyStatus[]>;
    companies: CompanyType[];
    setCompanies: (companies: CompanyType[]) => void;
    fetchCompanies: () => Promise<void>;
    selectedStatuses: string[];
    setSelectedStatuses: (statuses: string[]) => void;
    filteredCompanies: CompanyType[];
    operatorUsers: UserType[]; // Store operator users
    fetchOperatorUsers: () => void;
    selectedBusinessIds: string[];
    setSelectedBusinessIds: (ids: string[]) => void;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<MessageType[]>([]);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [company, setCompany] = useState<CompanyType | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [users, setUsers] = useState<UserType[]>([]);
    const [companyStatuses, setCompanyStatuses] = useState<CompanyStatus[]>([]);
    const [companies, setCompanies] = useState<CompanyType[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["הכל"]);
    const [operatorUsers, setOperatorUsers] = useState<UserType[]>([]);
    const [selectedBusinessIds, setSelectedBusinessIds] = useState<string[]>([]);

    const fetchOperatorUsers = async () => {
        try {
            const users = await getUsersByJobTitleId("מנהל תזרים");
            setOperatorUsers(users);
        } catch (error) {
            console.error("Failed to fetch operator users:", error);
        }
    };

    useEffect(() => {
        fetchOperatorUsers(); // Fetch once when the provider is mounted
    }, []);


    const filteredCompanies = useMemo(() => {
        let filtered = companies;
        if (selectedBusinessIds.length > 0) {
            filtered = filtered.filter((company) =>
                company.business_id && selectedBusinessIds.map(Number).includes(Number(company.business_id))
            );
        }
        if (selectedStatuses.includes("הכל")) return filtered;
        return filtered.filter((company) => selectedStatuses.includes(company.status.status));
    }, [companies, selectedStatuses, selectedBusinessIds]);

    const fetchCompanies = async () => {
        try {
            const data = await getActiveCompanies();
            const sortedCompanies = data.sort((a: CompanyType, b: CompanyType) =>
                a.name.localeCompare(b.name)
            );
            setCompanies(sortedCompanies);
        } catch (error) {
            console.error("Error fetching active companies:", error);
            setCompanies([]);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        const loadMessages = async () => {
            if (!companyId) return;

            setLoading(true); // ✅ Set loading before fetching
            await fetchMessages(companyId); // ✅ Wait for messages to be fetched
            setLoading(false); // ✅ Only set loading to false after fetch completes
        };

        loadMessages();
    }, [companyId]);

    // Fetch the company statuses only once when the provider starts
    useEffect(() => {
        const getStatuses = async () => {
            try {
                const data = await fetchCompanyStatuses();
                setCompanyStatuses(data);
            } catch (error) {
                console.error("Failed to fetch company statuses:", error);
            }
        };
        getStatuses();
    }, []); // Empty array ensures it only runs once when the component mounts


    const fetchUsersByCompany = async (companyId: string) => {
        try {
            setUsers([]); // Reset first
            const fetchedUsers = await getUsersByCompany(companyId);
            setUsers(fetchedUsers);
        } catch (error) {
            console.error("❌ Error fetching users:", error);
        }
    };

    const fetchMessages = async (companyId: string): Promise<void> => {
        try {
            setMessages([]);
            const msgs = await fetchMessagesByCompanyId(companyId);
            setMessages(msgs);
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    const fetchCompanyStatuses = async (): Promise<CompanyStatus[]> => {
        try {
            const statuses = await getCompanyStatuses();
            setCompanyStatuses(statuses);
            return statuses;
        } catch (error) {
            console.error("Error fetching company statuses:", error);
            return [];
        }
    };

    const sendMessage = async (companyId: string, content: string): Promise<MessageType | null> => {
        console.log("sendMessage: ", new Date().toLocaleTimeString());

        if (!content.trim()) return null; // Ensures an explicit return

        try {
            const response = await sendMessageToCompany(companyId, content);
            const fictiveMessageToAdd: MessageType = {
                id: response?.id ?? "",
                role: "user",
                content: content,
                created_at: response?.created_at,
            };
            if (response) {
                setMessages((prevMessages) => [...prevMessages, fictiveMessageToAdd]);
            } else {
                console.error("Failed to send message:");
            }
        } catch (error) {
            console.error("Error sending message:", error);
        }

        return null;
    };

    const executeRun = async (companyId: string) => {
        try {
            const assistantMessage: MessageType = {
                id: `assistant-${Date.now()}`,
                role: "assistant",
                content: "",
                created_at: Date.now(),
            };

            // Add fictive assistant message to UI
            setMessages((prevMessages) => [...prevMessages, assistantMessage]);

            let accumulatedText = ""; // Holds the full streamed message

            // Function to update assistant message during streaming
            const handleStreamUpdate = (textChunk: string) => {
                accumulatedText += textChunk; // Append new chunk to full message

                setMessages((prevMessages) =>
                    prevMessages.map((msg) =>
                        msg.id === assistantMessage.id
                            ? { ...msg, content: accumulatedText } // Update full message
                            : msg
                    )
                );
            };

            const lastUserMessage = messages.slice().reverse().find((msg) => msg.role === "user");
            const originalPrompt = lastUserMessage?.content || "";
            // Call API client function for streaming
            await executeRunApi(companyId, originalPrompt, handleStreamUpdate);

            console.log("✅ Streaming complete.");
        } catch (error) {
            console.error("❌ Error executing run:", error);
        }
    };

    const deleteMessage = async (companyId: string, messageId: string) => {
        try {
            const response = await deleteMessageApi(companyId, messageId);
            if (response.deleted) {
                setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== messageId));
                console.log(`✅ Message deleted: ${messageId}`);
            } else {
                console.error(`❌ Failed to delete message: ${messageId}`);
            }
        } catch (error) {
            console.error("❌ Error deleting message:", error);
        }
    };

    return (
        <ChatContext.Provider
            value={{
                loading,
                companyId,
                setCompanyId,
                messages,
                fetchMessages,
                sendMessage,
                executeRun,
                users,
                setUsers,
                fetchUsersByCompany,
                deleteMessage,
                company,
                setCompany,
                searchQuery,
                setSearchQuery,
                companyStatuses,
                fetchCompanyStatuses,
                companies,
                setCompanies,
                fetchCompanies,
                selectedStatuses,
                setSelectedStatuses,
                filteredCompanies, // ✅
                operatorUsers,
                fetchOperatorUsers,
                selectedBusinessIds,
                setSelectedBusinessIds
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};

// Custom hook for easy access
export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChat must be used within a ChatProvider");
    }
    return context;
};
